import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Award,
  Calendar,
  Trophy,
  QrCode,
  LogOut,
  User,
  Mail,
  Building,
  GraduationCap,
  TrendingUp,
  Clock,
  MapPin
} from 'lucide-react';
import { toast } from 'react-toastify';
import axios from '../utils/axios';
import QRCodeComponent from 'react-qr-code';

interface MemberData {
  id: number;
  memberId: string;
  fullName: string;
  email: string;
  department: string;
  university: string;
  totalPoints: number;
  achievementLevel: string;
  rankPosition: number | null;
  qrCode: string;
}

interface Event {
  id: number;
  title: string;
  description?: string;
  event_date: string;
  event_time: string;
  location: string;
  category?: string;
  points: number;
  status: string;
  brochure_url?: string;
  brochureUrl?: string;
}

interface Attendance {
  event_title: string;
  event_date: string;
  points_earned: number;
}

const MemberDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [member, setMember] = useState<MemberData | null>(null);
  const [upcomingEvents, setUpcomingEvents] = useState<Event[]>([]);
  const [attendanceHistory, setAttendanceHistory] = useState<Attendance[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMemberData();
    loadUpcomingEvents();
    loadAttendanceHistory();
  }, []);

  const loadMemberData = () => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setMember(JSON.parse(userData));
    } else {
      navigate('/member/login');
    }
    setLoading(false);
  };

  const loadUpcomingEvents = async () => {
    try {
      const response = await axios.get('/v1/events');
      console.log('Events API response:', response.data);
      // API returns data in response.data.data
      const eventsData = response.data.data || [];
      // Filter upcoming events
      const upcoming = eventsData
        .filter((event: Event) => event.status === 'upcoming')
        .slice(0, 3);
      setUpcomingEvents(upcoming);
    } catch (error) {
      console.error('Error loading events:', error);
    }
  };

  const loadAttendanceHistory = async () => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        const response = await axios.get(`/v1/attendance/member/${user.id}`);
        console.log('Attendance API response:', response.data);

        // Extract attendance data
        const attendanceData = response.data.data?.attendance || response.data.attendance || [];
        setAttendanceHistory(attendanceData.slice(0, 5));

        // Extract and update points from statistics
        if (response.data.data?.statistics) {
          const stats = response.data.data.statistics;
          const totalPoints = parseInt(stats.total_points || '0');
          console.log('Total points from API:', totalPoints);

          // Update member state with points
          setMember(prev => prev ? {
            ...prev,
            totalPoints: totalPoints
          } : null);
        }
      }
    } catch (error) {
      console.error('Error loading attendance:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userType');
    toast.success('Logged out successfully');
    navigate('/');
  };

  if (loading || !member) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Header */}
      <header className="bg-white border-b border-secondary-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-3 sm:py-4">
          <div className="flex justify-between items-center gap-2">
            <div className="min-w-0 flex-1">
              <h1 className="text-base sm:text-xl lg:text-2xl font-bold text-secondary-900 truncate">
                Hammad Foundation Portal
              </h1>
              <p className="text-xs sm:text-sm text-secondary-600 hidden sm:block">Volunteer Dashboard</p>
            </div>
            <button
              onClick={handleLogout}
              className="btn bg-secondary-100 text-secondary-700 hover:bg-secondary-200 flex items-center gap-1 sm:gap-2 px-3 sm:px-4 flex-shrink-0"
            >
              <LogOut className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Welcome Section */}
        <div className="mb-4 sm:mb-6 lg:mb-8">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-secondary-900">
            Welcome, {member.fullName}! 👋
          </h2>
          <p className="text-xs sm:text-sm text-secondary-600 mt-0.5 sm:mt-1">Member ID: {member.memberId}</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6 lg:mb-8">
          {/* Points Card */}
          <div className="card bg-gradient-to-br from-primary-500 to-primary-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-primary-100 text-xs sm:text-sm font-medium mb-1">Total Points</p>
                <p className="text-2xl sm:text-3xl lg:text-4xl font-bold">{member.totalPoints || 0}</p>
              </div>
              <div className="bg-white/20 rounded-full p-2 sm:p-3">
                <Award className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8" />
              </div>
            </div>
          </div>

          {/* Achievement Level Card */}
          <div className="card bg-gradient-to-br from-yellow-500 to-orange-500 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-100 text-xs sm:text-sm font-medium mb-1">Achievement</p>
                <p className="text-xl sm:text-2xl font-bold">{member.achievementLevel || 'Bronze'}</p>
              </div>
              <div className="bg-white/20 rounded-full p-2 sm:p-3">
                <Trophy className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8" />
              </div>
            </div>
          </div>

          {/* Rank Card */}
          <div className="card bg-gradient-to-br from-blue-500 to-purple-500 text-white sm:col-span-2 lg:col-span-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-xs sm:text-sm font-medium mb-1">Rank</p>
                <p className="text-2xl sm:text-3xl lg:text-4xl font-bold">
                  {member.rankPosition ? `#${member.rankPosition}` : '-'}
                </p>
              </div>
              <div className="bg-white/20 rounded-full p-2 sm:p-3">
                <TrendingUp className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8" />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {/* Main Content - Left Side */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Profile Info */}
            <div className="card">
              <h3 className="text-base sm:text-lg lg:text-xl font-bold text-secondary-900 mb-3 sm:mb-4 flex items-center gap-2">
                <User className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600" />
                Profile Information
              </h3>
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 bg-secondary-50 rounded-lg">
                  <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-secondary-600 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] sm:text-xs text-secondary-600">Email</p>
                    <p className="text-xs sm:text-sm font-medium text-secondary-900 truncate">{member.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 bg-secondary-50 rounded-lg">
                  <Building className="h-4 w-4 sm:h-5 sm:w-5 text-secondary-600 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] sm:text-xs text-secondary-600">Department</p>
                    <p className="text-xs sm:text-sm font-medium text-secondary-900">{member.department}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 bg-secondary-50 rounded-lg">
                  <GraduationCap className="h-4 w-4 sm:h-5 sm:w-5 text-secondary-600 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] sm:text-xs text-secondary-600">University</p>
                    <p className="text-xs sm:text-sm font-medium text-secondary-900">{member.university}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Upcoming Events */}
            <div className="card">
              <h3 className="text-base sm:text-lg lg:text-xl font-bold text-secondary-900 mb-3 sm:mb-4 flex items-center gap-2">
                <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600" />
                Upcoming Events
              </h3>
              {upcomingEvents.length > 0 ? (
                <div className="space-y-3 sm:space-y-4">
                  {upcomingEvents.map((event) => (
                    <div
                      key={event.id}
                      className="border border-secondary-200 rounded-lg hover:border-primary-500 hover:shadow-md transition-all overflow-hidden"
                    >
                      {/* Event Brochure Image */}
                      {(event.brochure_url || event.brochureUrl) && (
                        <div className="relative w-full h-32 sm:h-40">
                          <img
                            src={event.brochure_url || event.brochureUrl}
                            alt={event.title}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                          <div className="absolute bottom-2 left-2 right-2">
                            <h4 className="text-sm sm:text-base font-bold text-white line-clamp-1">
                              {event.title}
                            </h4>
                          </div>
                        </div>
                      )}

                      {/* Event Details */}
                      <div className="p-3 sm:p-4">
                        {!(event.brochure_url || event.brochureUrl) && (
                          <h4 className="text-sm sm:text-base font-semibold text-secondary-900 mb-2">
                            {event.title}
                          </h4>
                        )}

                        {event.description && (
                          <p className="text-xs sm:text-sm text-secondary-600 mb-2 line-clamp-2">
                            {event.description}
                          </p>
                        )}

                        <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-secondary-600">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                            {new Date(event.event_date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </span>
                          {event.event_time && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                              {event.event_time}
                            </span>
                          )}
                        </div>

                        {event.location && (
                          <div className="flex items-center gap-1 text-xs sm:text-sm text-secondary-600 mt-1">
                            <MapPin className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                            <span className="truncate">{event.location}</span>
                          </div>
                        )}

                        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-secondary-100">
                          {event.category && (
                            <span className="badge bg-purple-100 text-purple-700 text-[10px] sm:text-xs">
                              {event.category.replace('_', ' ').toUpperCase()}
                            </span>
                          )}
                          <span className="badge bg-primary-100 text-primary-700 whitespace-nowrap text-[10px] sm:text-xs">
                            <Award className="h-3 w-3 inline mr-1" />
                            {event.points} pts
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs sm:text-sm text-secondary-600 text-center py-6 sm:py-8">No upcoming events</p>
              )}
            </div>

            {/* Attendance History */}
            <div className="card">
              <h3 className="text-base sm:text-lg lg:text-xl font-bold text-secondary-900 mb-3 sm:mb-4 flex items-center gap-2">
                <Award className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600" />
                Recent Attendance
              </h3>
              {attendanceHistory.length > 0 ? (
                <div className="space-y-2">
                  {attendanceHistory.map((attendance, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center gap-2 p-2.5 sm:p-3 bg-secondary-50 rounded-lg"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm font-medium text-secondary-900 truncate">{attendance.event_title}</p>
                        <p className="text-[10px] sm:text-xs text-secondary-600">
                          {new Date(attendance.event_date).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="badge bg-primary-100 text-primary-700 whitespace-nowrap text-[10px] sm:text-xs">
                        +{attendance.points_earned} pts
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs sm:text-sm text-secondary-600 text-center py-6 sm:py-8">
                  No attendance records yet
                </p>
              )}
            </div>
          </div>

          {/* Sidebar - Right Side */}
          <div className="space-y-4 sm:space-y-6">
            {/* QR Code */}
            <div className="card text-center">
              <h3 className="text-base sm:text-lg lg:text-xl font-bold text-secondary-900 mb-3 sm:mb-4 flex items-center justify-center gap-2">
                <QrCode className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600" />
                Your QR Code
              </h3>
              <p className="text-xs sm:text-sm text-secondary-600 mb-3 sm:mb-4">
                Show this QR code at events for attendance
              </p>
              {member.qrCode && (
                <div className="flex justify-center mb-3 sm:mb-4">
                  <div className="bg-white p-3 sm:p-4 rounded-lg border-2 border-primary-200">
                    <QRCodeComponent
                      value={member.qrCode}
                      size={window.innerWidth < 640 ? 160 : 200}
                      level="H"
                    />
                  </div>
                </div>
              )}
              <p className="text-[10px] sm:text-xs text-secondary-500 mt-2">Member ID: {member.memberId}</p>
            </div>

            {/* Achievement Badge */}
            <div className="card bg-gradient-to-br from-primary-50 to-primary-100 border-2 border-primary-200">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-primary-600 rounded-full mb-2 sm:mb-3">
                  <Trophy className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 text-white" />
                </div>
                <h4 className="text-base sm:text-lg font-bold text-secondary-900 mb-1.5 sm:mb-2">
                  {member.achievementLevel} Member
                </h4>
                <p className="text-xs sm:text-sm text-secondary-600 mb-3 sm:mb-4">
                  You're doing great! Keep attending events to level up.
                </p>
                <div className="flex justify-center gap-2">
                  <div className="badge bg-white text-secondary-700 text-xs sm:text-sm">
                    {member.totalPoints || 0} points
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberDashboard;
